/**
 * echDynPick — the DYNAMIC FEATURE PICKER's decision logic, ported from the
 * monolith's `__dynModal` (Projects/Email/Smart Email Request/index.html
 * L10278–10542) and `__dynThumbs` (L10245–10275). 2026-08-20.
 *
 * WHY A SEPARATE PURE MODULE. In the monolith the wizard is one 260-line closure
 * that computes its caps and labels, fetches packs, mutates `window.__dyn`, and
 * writes innerHTML — all in the same function. The parts worth porting exactly are
 * the DECISIONS (which steps, which cap, which button label, which cards are
 * disabled), and those are the parts hardest to see inside string concatenation.
 * They live here as pure functions so a test can pin them to the monolith's own
 * numbers, and `DynFeaturePicker.tsx` is left holding only fetching and markup.
 * Nothing in this file touches the DOM, the network, or a global.
 *
 * WHAT THE MONOLITH KEEPS IN GLOBALS, AND WHERE IT GOES HERE:
 *   window.__dyn.secLevel[sec]  → the `level` argument (owned by the caller)
 *   window.__dynWiz             → the `wiz` boolean (owned by the caller)
 *   window.__dyn[kind]          → the `sel` array (owned by the caller)
 *   window.__lvPackCache        → the `packs` argument (the repo's Pack[])
 *
 * KEY DIVERGENCE, DELIBERATE: home pick keys use this repo's `homeKey()` from
 * dynamicFill (`<code>|h|<addr>|<price>`), NOT the monolith's `__homeKey`
 * (`<code>|<lot>`). The repo's `/sfmc-content` HomesiteInfo carries no `lot`
 * field at all (src/types/api.ts), and `itemsForLevel`/`rowsForLevel` already
 * filter on `homeKey`. Emitting monolith-shaped keys here would produce picks that
 * the fill functions silently drop.
 */
import { DYN_MAX_HOMES, planImg, homeKey, notSold, type Pack, type FillLevel } from "./dynamicFill";
import type { PlanInfo, HomesiteInfo } from "../types/api";
import { echShortLot, echParentCommunity } from "./echDyn";

/** what the picker is picking. `brand` and `heroes` share the same modal in the
 *  monolith, which is why they share the cap table (L10286). */
export type PickKind = "comms" | "plans" | "homes" | "heroes" | "brand";

/** the FEATURE LEVEL of the section that opened the wizard — `window.__dyn.secLevel[sec]`.
 *  Same three values as dynamicFill's FillLevel, and deliberately the same type. */
export type WizLevel = FillLevel;

/** true when the picker was opened from a section (fp/lst) rather than standalone */
export interface WizCtx { wiz: boolean; level?: WizLevel }

/* ── monolith TITLES map (L10292). `brand` keeps the &-entity spelled out. ─────── */
const TITLES: Record<PickKind, string> = {
  comms: "Choose communities",
  homes: "Choose homes",
  plans: "Choose floorplans",
  heroes: "Choose hero images",
  brand: "Who drives branding & contact?",
};
export const pickTitle = (kind: PickKind): string => TITLES[kind] || "Choose";

/* ── the breadcrumb / step labels (monolith L10471, inlined map) ───────────────── */
const STEP_LABELS: Record<WizLevel, string> = { comms: "Communities", plans: "Floorplans", homes: "Homesites" };

/* ── the pane's header noun (monolith __dynPane L12139) ────────────────────────── */
const PANE_NOUNS: Record<PickKind, string> = {
  heroes: "Images", homes: "Homes", plans: "Floorplans", comms: "Communities", brand: "Branding",
};
export const paneNoun = (kind: PickKind): string => PANE_NOUNS[kind] || "Selected";

/**
 * THE STEP CHAIN — monolith `wizSteps()` (L10284):
 *   comms → ["comms"] · plans → ["comms","plans"] · homes → ["comms","plans","homes"]
 *
 * The chain is derived from the LEVEL, never accumulated as the user walks it, so
 * flipping the FEATURE LEVEL control mid-wizard re-shapes the remaining steps
 * immediately (that is why the primary button can flip from "Next" to "Done"
 * without leaving the community step).
 */
export function wizSteps(level: WizLevel): WizLevel[] {
  /* ── ONE STEP PER LEVEL (2026-08-22) ──────────────────────────────────────────────
     Jack: "why are you showing floor plan next — each level should be completely
     separate as i should be able to showcase communities, floor plans or homesites on
     this table if desired." The chain (comms → plans → homes) existed because an item
     step had no inventory until a community was picked; the division feeds removed that
     dependency, and the scope dropdown does the narrowing without leaving the step. So a
     level is now exactly what it says: pick communities, or floorplans, or homesites, and
     press Done. The FEATURE LEVEL control switches between them.
     Kept as a function returning an array because everything downstream (breadcrumb,
     primaryLabel, isLast) reads it, and a one-item chain answers all three correctly. */
  return [level];
}

/**
 * THE CAPS — monolith `capFor()` (L10286), transcribed branch for branch:
 *
 *   k==="homes" ? DYN_MAX_HOMES
 *   : k==="brand" ? 1
 *   : k==="plans" ? (wiz ? 10 : 6)
 *   : k==="comms" ? ((wiz && wizLevel()!=="comms") ? 10 : 6)
 *   : 6
 *
 * The comms branch is the one that gets mis-ported: the 10 is gated on a wizard
 * BEING OPEN as well as on its level, so a standalone community picker is 6 even
 * though a `level` may be sitting in state. The rationale (monolith comment
 * L10281): 6 communities is the featuring cap, but pulling plans/homesites that
 * span many communities needs a wider net to draw from.
 */
export function capFor(kind: PickKind, ctx: WizCtx): number {
  if (kind === "homes") return DYN_MAX_HOMES;
  if (kind === "brand") return 1;
  if (kind === "plans") return ctx.wiz ? 10 : 6;
  if (kind === "comms") return ctx.wiz && ctx.level !== "comms" ? 10 : 6;
  return 6;
}

export interface Crumb { index: number; total: number; parts: { label: string; current: boolean }[] }

/**
 * "Step 1 of 2 · **Communities** › Floorplans" — monolith L10471.
 * NULL on a single-step chain: the monolith gates the whole crumb on
 * `steps.length>1`, so a Community-level wizard shows no breadcrumb at all.
 * `index` is 1-based to match the rendered copy.
 */
export function breadcrumb(steps: WizLevel[], kind: WizLevel): Crumb | null {
  if (steps.length <= 1) return null;
  const i = steps.indexOf(kind);
  if (i < 0) return null;
  return { index: i + 1, total: steps.length, parts: steps.map((s) => ({ label: STEP_LABELS[s], current: s === kind })) };
}

/**
 * The primary button — monolith footBtns (L10486): "Done" on the last step of the
 * chain, otherwise "Next: <next step's label> ›". A kind that is not in the chain
 * (heroes/brand, which never run as a wizard) reads Done.
 */
export function primaryLabel(steps: WizLevel[], kind: PickKind): string {
  const i = steps.indexOf(kind as WizLevel);
  if (i < 0 || i === steps.length - 1) return "Done";
  return "Next: " + STEP_LABELS[steps[i + 1]] + " ›";
}

/** monolith L10497: the community step searches, every other step filters */
export const searchPlaceholder = (kind: PickKind): string => (
  kind === "comms" ? "Search your division…"
    : kind === "homes" ? "Search by community, street address or homesite #…"
      : kind === "plans" ? "Search by community, plan name or plan #…"
        : "Filter…");

/**
 * The line under the modal title — monolith L10493.
 * Three cases, and the comms one splits on whether a plans/homes wizard is driving:
 * inside one, the communities are a SOURCE for the picks two steps later, so the
 * copy says so and quotes the (wider) cap.
 */
export function pickSubtitle(kind: PickKind, o: { wiz: boolean; level?: WizLevel; cap: number; n: number; direct?: boolean }): string {
  /* DIRECT ENTRY (2026-08-22): the list is the whole division, so the copy must not
     imply a community was chosen first — and it names what the box searches, because
     "homesite number" is the field a requester arrives holding. */
  if (o.direct && kind === "homes") return "Every available home in your division — search by community, address or homesite #.";
  if (o.direct && kind === "plans") return "Every floorplan in your division, selling now and coming soon — search by community, plan name or plan #.";
  if (kind === "comms") {
    return o.wiz && o.level !== "comms"
      ? "Pick the communities your plans & homes come from — up to " + o.cap + "."
      : "Their homes, plans & heroes flow into this email.";
  }
  return "Tap to select — " + o.n + " of " + o.cap + (kind === "heroes" ? " · 2+ becomes a rotating carousel" : "");
}

/**
 * The footer's "· max N" suffix — monolith L10500: `(kind!=="comms"||wiz)`.
 * A STANDALONE community picker prints "N selected" with no max, because 6 is the
 * template's featuring capacity rather than a slot budget the requester is
 * spending. Every other surface shows the max. (The brief for this port said the
 * footer always reads "N selected · max M"; the monolith disagrees.)
 */
export const footerMax = (kind: PickKind, cap: number, wiz: boolean): string =>
  kind !== "comms" || wiz ? " · max " + cap : "";

/** the refusal toast — monolith wireGrid (L10534 comms / L10540 cards) */
export const capMessage = (kind: PickKind, cap: number): string =>
  kind === "comms" ? "Up to " + cap + " communities per email" : "All " + cap + " slots are used — remove one first";

export interface PickResult { sel: string[]; refused: boolean; message?: string }

/**
 * TOGGLE ONE PICK, WITH THE CAP ACTUALLY ENFORCED — monolith wireGrid (L10528–10542).
 *
 * Three behaviours in one function, all of them the monolith's:
 *  1. an already-picked key always comes OUT, even at the cap (that is how you make
 *     room), so the cap is only ever checked on the ADD path;
 *  2. `brand` is single-select — the array is emptied and replaced;
 *  3. an add that would exceed the cap is REFUSED and the caller toasts. The
 *     monolith returns early without mutating; so do we. Greying the card out is
 *     not enough on its own: `groupedCards` disables it visually, this refuses it.
 */
export function addPick(sel: string[], key: string, cap: number, kind: PickKind): PickResult {
  const at = sel.indexOf(key);
  if (kind === "brand") return { sel: at > -1 ? [] : [key], refused: false };
  if (at > -1) { const next = sel.slice(); next.splice(at, 1); return { sel: next, refused: false }; }
  if (sel.length >= cap) return { sel, refused: true, message: capMessage(kind, cap) };
  return { sel: sel.concat([key]), refused: false };
}

/* ── card view models ──────────────────────────────────────────────────────────── */

export interface DynCardVM {
  key: string;
  name: string;
  meta: string;
  /** "" means no usable photo — the caller renders the placeholder tile, NOT an <img src=""> */
  img: string;
  price: number;
  /** the struck-through prior price; 0 when there is no drop */
  was: number;
  badge: boolean;
  /** the feed's homesite status — LIST view shows it as a picker-only chip
   *  ("Move-in ready" / "Under constr."); it never reaches the email (Jack,
   *  2026-08-21: "make sure you don't add junk in like Move-in ready") */
  status: string;
  on: boolean;
  /** greyed + unclickable: at the cap and not already picked (monolith `!on&&full`) */
  disabled: boolean;
}
export interface DynCardGroup { code: string; name: string; cards: DynCardVM[] }

const num = (v: unknown): string => (v ? Number(String(v).replace(/[^0-9.]/g, "")).toLocaleString() : "");

/** monolith L10433: "5 bd · 3 ba · 2281 sqft" — raw sqft, NOT thousands-separated */
export const planCardMeta = (pl: PlanInfo): string =>
  (pl.beds || "—") + " bd · " + (pl.baths || "—") + " ba · " + (pl.sqft || "—") + " sqft";

/** monolith L10426: "2,281 / 5 / 3 · Katy" — slashes, and the sqft IS separated here */
export const homeCardMeta = (hm: HomesiteInfo, packName: string): string =>
  (hm.sqft ? num(hm.sqft) : "—") + " / " + (hm.beds || "—") + " / " + (hm.baths || "—") + " · " + (hm.city || packName);

/**
 * THE CARD GRID, GROUPED BY COMMUNITY — monolith cardsHtml() (L10419–10438).
 *
 * Three rules that matter and are easy to lose:
 *  1. GROUPING IS PER PACK, in pack order, with the community name as an uppercase
 *     heading. A community whose cards all filtered out is DROPPED (`if(cards.length)`),
 *     not rendered as an empty group — that is why the caller can trust
 *     `groups.length === 0` to mean "nothing matches".
 *  2. SOLD HOMES ARE EXCLUDED at the source (`__notSold`, L9559). Floorplans are
 *     NOT status-filtered — the monolith doesn't, and a plan has no sold state.
 *  3. THE CAP DISABLES UNPICKED CARDS ONLY. A picked card at the cap stays live so
 *     it can be removed.
 */
/**
 * THE LIST VIEW AS A TABLE — HOMES AND PLANS ONLY. 2026-08-22.
 *
 * Jack approved format "A1" from the mockups: equal columns, everything centred, and
 * the ONE variable-length column (address / plan name) left-aligned with its header,
 * because centring a string whose length changes row to row drifts — measured 8.5px on
 * real AZH addresses. This returns the CELLS for those rows so the picker renders a
 * real table instead of a name + meta line, and so the HOMESITE NUMBER gets a column
 * of its own: it was missing from the list entirely, which is the defect Jack opened
 * with ("missing homesite number").
 *
 * Community level is deliberately NOT here (Jack, 2026-08-22: "don't need it for
 * communities though just floor plans and homesites") — that step keeps its cards.
 */
export interface DynTableCell { text: string; kind: "id" | "name" | "spec" | "price" | "chip"; chip?: "ok" | "warn" | "cs" }
export interface DynTableRow { key: string; on: boolean; disabled: boolean; cells: DynTableCell[] }
export interface DynTableGroup { code: string; name: string; rows: DynTableRow[] }

/**
 * The column labels, in order.
 *
 * ── PLAN NAME IS THE FAR-LEFT COLUMN ON BOTH LEVELS (2026-08-22, Jack: "i'd like to
 * add plan name in the far left column for both floor plan and homesite") ──
 * Floorplans led with the plan NUMBER and homesites had no plan at all, so a requester
 * scanning the list could not tell which plan a home was without opening it. The plan
 * name is the thing he actually reads, so it leads both tables and the number follows.
 *
 * EVERY column is centred — Jack's standing table rule — so there is no longer a
 * left-aligned name column (`TABLE_NAME_COL` is gone; the cells ellipsis instead).
 */
export function tableHead(kind: PickKind): string[] {
  return kind === "homes"
    ? ["Plan", "Homesite", "Address", "Sq ft", "Bd / Ba", "Price", "Status"]
    : ["Plan", "Plan #", "Sq ft", "Bd / Ba", "From", "Status"];
}

const tnum = (v: unknown): string => {
  const n = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return isFinite(n) && n ? n.toLocaleString("en-US") : "\u2014";
};
const tmoney = (v: unknown): string => {
  const n = Number(v);
  return isFinite(n) && n ? "$" + n.toLocaleString("en-US") : "\u2014";
};
const tbdba = (b: unknown, ba: unknown): string => {
  const f = (x: unknown) => { const n = Number(x); return isFinite(n) && n ? String(n) : "\u2014"; };
  return f(b) + " / " + f(ba);
};

export function groupedTable(
  packs: Pack[],
  kind: PickKind,
  q: string,
  sel: string[],
  o: { cap?: number } = {},
): DynTableGroup[] {
  /* the search and the grouping are groupedCards' own — ONE matcher, ONE order, so
     the table and the card grid can never disagree about what a query returns */
  const cards = groupedCards(packs, kind, q, sel, o);
  const out: DynTableGroup[] = [];
  cards.forEach((g) => {
    const pack = packs.find((p) => p.code === g.code);
    if (!pack) return;
    const rows: DynTableRow[] = [];
    g.cards.forEach((c) => {
      if (kind === "homes") {
        const hm = (pack.C.homesites || []).find((h) => homeKey(pack.code, h) === c.key) as (HomesiteInfo & { lot?: string }) | undefined;
        if (!hm) return;
        const mir = /MOVE.?IN.?READY/i.test(String(hm.status || ""));
        rows.push({ key: c.key, on: c.on, disabled: !!c.disabled, cells: [
          { text: String(hm.plan || "").trim() || "\u2014", kind: "name" },
          { text: echShortLot(hm.lot, pack.code) || "\u2014", kind: "id" },
          { text: hm.addr || hm.plan || "Home", kind: "name" },
          { text: tnum(hm.sqft), kind: "spec" },
          { text: tbdba(hm.beds, hm.baths), kind: "spec" },
          { text: tmoney(hm.price), kind: "price" },
          { text: mir ? "Move-in ready" : "Under constr.", kind: "chip", chip: mir ? "ok" : "warn" },
        ] });
      } else {
        const idx = Number(String(c.key).split("|")[2]);
        const pl = (pack.C.plans || [])[idx];
        if (!pl) return;
        rows.push({ key: c.key, on: c.on, disabled: !!c.disabled, cells: [
          { text: pl.name || "Plan", kind: "name" },
          { text: String(pl.num || "\u2014"), kind: "id" },
          { text: tnum(pl.sqft), kind: "spec" },
          { text: tbdba(pl.beds, pl.baths), kind: "spec" },
          { text: tmoney(pl.price), kind: "price" },
          /* coming-soon plans are listed now (2026-10-22), so the table says which is which —
             the same chip column the homesites table already carries */
          String(pl.status || "").toUpperCase() === "COMING_SOON"
            ? { text: "Coming soon", kind: "chip", chip: "cs" }
            : { text: "Selling now", kind: "chip", chip: "ok" },
        ] });
      }
    });
    if (rows.length) out.push({ code: g.code, name: g.name, rows });
  });
  return out;
}

export function groupedCards(
  packs: Pack[],
  kind: PickKind,
  q: string,
  sel: string[],
  o: { cap?: number } = {},
): DynCardGroup[] {
  const needle = String(q || "").toLowerCase();
  const cap = o.cap;
  const full = cap != null && sel.length >= cap;
  const out: DynCardGroup[] = [];
  const mk = (key: string, name: string, meta: string, img: string, price = 0, was = 0, badge = false, status = ""): DynCardVM => {
    const on = sel.indexOf(key) > -1;
    return { key, name, meta, img, price, was, badge, status, on, disabled: !on && full };
  };

  /* ── WHAT THE SEARCH BOX MATCHES (widened 2026-08-22) ──────────────────────────
     Jack, on jumping straight into a level: "should be able to search for them in
     the search bar via name, street address, and homesite number like 26254600130."
     The box used to match the card's TITLE only — the street address for a home, the
     plan name for a plan — which is unusable on a division-wide list where the thing
     you know is the lot number or the community.
     So a needle now matches any of: community name, street address, plan name, plan
     number, the full homesite id, and the ADVERTISED homesite number (its last four
     digits with leading zeros stripped — the form Jack reads off a spec sheet: "it
     would be 90, 173, 156"). Digits-only needles are compared against the digits of
     the lot so a typed "26254600130" matches regardless of separators. */
  const hay = (...parts: (string | number | undefined | null)[]) =>
    parts.map((x) => String(x == null ? "" : x).toLowerCase()).filter(Boolean);
  const hit = (needleLc: string, parts: (string | number | undefined | null)[]): boolean => {
    if (!needleLc) return true;
    const hs = hay(...parts);
    if (hs.some((h) => h.indexOf(needleLc) > -1)) return true;
    /* a pure-digit needle also matches the advertised (last-4, zeros stripped) form */
    if (/^\d+$/.test(needleLc)) {
      return hs.some((h) => {
        const d = h.replace(/\D/g, "");
        if (!d) return false;
        if (d.indexOf(needleLc) > -1) return true;
        const tail = d.slice(-4).replace(/^0+/, "");
        return !!tail && tail === needleLc;
      });
    }
    return false;
  };

  packs.forEach((p) => {
    const cards: DynCardVM[] = [];
    if (kind === "homes") {
      (p.C.homesites || []).filter(notSold).forEach((hm) => {
        const nm = hm.addr || hm.plan || "Home";
        const lot = (hm as { lot?: string }).lot;
        if (!hit(needle, [p.name, hm.addr, hm.plan, hm.city, lot])) return;
        /* the PRICE DROP badge and the struck price are the same condition — a
           wasPrice strictly above the current price (monolith L10426) */
        const drop = !!(hm.wasPrice && hm.price && hm.wasPrice > hm.price);
        cards.push(mk(homeKey(p.code, hm), nm, homeCardMeta(hm, p.name), hm.img || "", hm.price || 0, drop ? hm.wasPrice! : 0, drop, String(hm.status || "")));
      });
    } else if (kind === "plans") {
      (p.C.plans || []).forEach((pl, idx) => {
        const nm = pl.name || "Plan";
        if (!hit(needle, [p.name, pl.name, pl.num])) return;
        cards.push(mk(p.code + "|p|" + idx, nm, planCardMeta(pl), planImg(pl), pl.price || 0, 0, false, String(pl.status || "")));
      });
    } else if (kind === "heroes") {
      /* one hero per community, so the monolith renders heroes as ONE flat grid with
         no group headings (L10409). Kept as a single-card group per pack; the caller
         flattens. The meta line says whether it is in the rotation. */
      const hero = (p.C.community && p.C.community.hero) || "";
      if (hero && (!needle || p.name.toLowerCase().indexOf(needle) > -1)) {
        const kh = p.code + "|hero";
        const on = sel.indexOf(kh) > -1;
        cards.push(mk(kh, p.name, on && sel.length > 1 ? "In the carousel" : "Community hero", hero));
      }
    }
    if (cards.length) out.push({ code: p.code, name: p.name, cards });
  });
  return out;
}

/* ── the selected-cards panel (monolith __dynThumbs, L10245–10275) ─────────────── */

export interface DynTileVM { key: string; name: string; meta: string; img: string }

/**
 * THE PICKED TILES, IN FEED ORDER — monolith __dynThumbs (L10256–10258).
 *
 * NOT in pick order: the monolith walks the packs and keeps whatever is in the
 * picked list, so the panel always reads in the same order as the feed no matter
 * what order the requester tapped in. Capped at the listing slots (`out.slice(0,
 * DYN_MAX_HOMES)`) and, like the grid, sold homes are filtered out first — a home
 * that sells between two page loads drops out of the panel on its own.
 */
export function selectedTiles(packs: Pack[], kind: "plans" | "homes", picked: string[]): DynTileVM[] {
  const out: DynTileVM[] = [];
  packs.forEach((p) => {
    if (kind === "homes") {
      (p.C.homesites || []).filter(notSold).forEach((hm) => {
        const k = homeKey(p.code, hm);
        if (picked.indexOf(k) < 0) return;
        out.push({
          key: k, img: hm.img || "", name: hm.addr || hm.plan || "Home",
          meta: (hm.city ? hm.city + " · " : "") + (hm.sqft ? num(hm.sqft) + " sq ft" : "") + (hm.beds ? " · " + hm.beds + " bd" : ""),
        });
      });
    } else {
      (p.C.plans || []).forEach((pl, i) => {
        const k = p.code + "|p|" + i;
        if (picked.indexOf(k) < 0) return;
        out.push({
          key: k, img: planImg(pl), name: pl.name || "Plan",
          meta: p.name + " · " + (pl.sqft ? num(pl.sqft) + " sq ft" : "") + (pl.beds ? " · " + pl.beds + " bd" : ""),
        });
      });
    }
  });
  return out.slice(0, DYN_MAX_HOMES);
}

/** the "+ Add a …" button label — monolith L10273 */
export const addMoreLabel = (kind: "plans" | "homes"): string => (kind === "homes" ? "+ Add a home" : "+ Add a floorplan");

/**
 * Which SECTION a pick kind belongs to — monolith close() (L10350) and the pane's
 * `data-dynwiz` wiring (L12218): plans drive the Featured block (fp), homes the
 * Listing table (lst). Used to route a finished wizard back at its section.
 */
export const secForKind = (kind: PickKind): "fp" | "lst" | "hero" =>
  kind === "homes" ? "lst" : kind === "plans" ? "fp" : "hero";

/* ═══════════════════════════════════════════════════════════════════════════════
   THE WIZARD'S KEYS → A STABLE PICK AN ECH BLOCK CAN STORE. 2026-08-20.
   ═══════════════════════════════════════════════════════════════════════════════

   The picker's own plan key is `<code>|p|<idx>` (L248/L293 above) — a 0-BASED INDEX
   into `pack.C.plans`. That is fine while the modal is open and the array is the
   one it just rendered, and it is NOT fine to persist on a block: `/sfmc-content`
   is refetched, a plan sells out, an order changes, and a stored `2` silently
   becomes a different floorplan in a live send. `echDyn`'s `__planPick` therefore
   stores the plan's OWN identifiers, and this is the one place that translates.

   HOME PICKS NEED NO TRANSLATION. `homeKey()` is already `<code>|h|<addr>|<price>`
   — no index in it — so `__homePick` stores the wizard's keys VERBATIM. That is
   also why `echDyn` imports `homeKey` rather than re-spelling the format: one
   definition, read and written.

   NEITHER FUNCTION INVENTS AN IDENTIFIER. A key that does not resolve against the
   packs in hand returns "" rather than a plausible-looking string — an empty pick
   falls back to the positional behaviour, which is a real floorplan from the right
   community; a fabricated one would be read as a deliberate choice. */

/** the picker's `<code>|p|<idx>` → `__planPick`'s `<code>|pn|<num>|<name>`, or "" */
export function planPickKey(packs: Pack[], pickerKey: string): string {
  const p = String(pickerKey || "").split("|");
  if (p.length < 3 || p[1] !== "p") return "";
  const pack = packs.find((x) => x.code === p[0]);
  if (!pack) return "";
  const pl: PlanInfo | undefined = (pack.C.plans || [])[Number(p[2])];
  if (!pl) return "";
  /* num FIRST, name second — `echPlanFor` tries them in that order, and a plan with
     neither is not identifiable at all, so it is refused rather than half-stored */
  const num = String(pl.num == null ? "" : pl.num).trim();
  const name = String(pl.name || "").trim();
  if (!num && !name) return "";
  return pack.code + "|pn|" + num + "|" + name;
}

/**
 * THE WIZARD'S PLAN KEYS → `__planPick`'s NEWLINE-JOINED LIST. 2026-08-28.
 *
 * WHY IT DID NOT EXIST, AND WHAT THAT COST. `planPickKey` above stores ONE plan, and
 * for a Featured card that is the whole truth — a card holds one floorplan. The
 * Listing table is ten rows, and the wizard offers it a Floorplans level with a cap of
 * ten, so a requester could select four floorplans, press Done, and have three of them
 * discarded with no message: `Inspector` wrote `picks.plans[0]` and nothing else. The
 * table then rendered a collection heading and zero rows, because no fill path read a
 * plan pick for ROWS at all. That is Jack's "floor plans aren't showing nor saving
 * when check marking them" — both halves of it, in one sentence.
 *
 * So plans get the same shape homes have had since 2026-08-22: a grouped list, one
 * stored key per line, communities in first-touch order and each community's plans in
 * the order they were chosen. Grouped for the same reason — the section headings are
 * welded to row spans, so rows have to be contiguous per community or a heading names
 * somebody else's floorplan.
 *
 * EACH LINE IS STILL `planPickKey`'s OWN FORMAT — `<code>|pn|<num>|<name>`, never the
 * picker's 0-based index, for the reason the block comment above gives. One line is a
 * valid list, so every `__planPick` written before today reads back unchanged.
 */
export function planPickList(packs: Pack[], pickerKeys: string[]): string {
  const order: string[] = [];
  const byParent: Record<string, string[]> = {};
  const seen: Record<string, 1> = {};
  (pickerKeys || []).forEach((k) => {
    /* one translator, the same one the single-pick path uses — a key that does not
       resolve against the packs in hand is refused rather than half-stored */
    const stored = planPickKey(packs, String(k || "").trim());
    if (!stored || seen[stored]) return;
    seen[stored] = 1;
    const code = String(stored.split("|")[0] || "").trim();
    const pack = packs.find((p) => String(p.code) === code);
    const par = (pack ? pickParentOf(pack) : "").toLowerCase() || code || "?";
    if (!byParent[par]) { byParent[par] = []; order.push(par); }
    byParent[par].push(stored);
  });
  const out: string[] = [];
  order.forEach((par) => byParent[par].forEach((k) => out.push(k)));
  return out.join("\n");
}

/**
 * "+ ADD A HOME" ON A FLOORPLANS TABLE APPENDS TOO (2026-09-15). The homes side has had
 * `homePickMerge` since 2026-08-22; the plans side went through `planPickList`, which
 * REPLACES — so on a plans-level table a section add swapped the one row out for the
 * new plan (driven on dev: Conway → Edison, and the requester's typed price stayed on
 * row 1 under the wrong plan). Same shape as the homes merge: the stored list is
 * trusted, only the new keys are resolved, grouped by parent in first-touch order.
 */
export function planPickMerge(packs: Pack[], storedRaw: unknown, add: string[]): string {
  const stored = String(storedRaw || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const fresh = planPickList(packs, add || []).split("\n").filter(Boolean);
  const all: string[] = [];
  stored.concat(fresh).forEach((k) => { if (all.indexOf(k) < 0) all.push(k); });
  const order: string[] = [];
  const byPar: Record<string, string[]> = {};
  all.forEach((k) => {
    const code = String(k).split("|")[0] || "?";
    const pack = packs.find((p) => String(p.code) === code);
    const par = (pack ? pickParentOf(pack) : code).toLowerCase();
    if (!byPar[par]) { byPar[par] = []; order.push(par); }
    byPar[par].push(k);
  });
  const out: string[] = [];
  order.forEach((par) => byPar[par].forEach((k) => out.push(k)));
  return out.join("\n");
}

/**
 * The parent COMMUNITY a pack belongs to — a masterplan's 50s and 60s are two SFMC
 * collections but one community to a reader. Same rule `echGroupedListingPatch` uses,
 * so the picked path and the fed path group identically.
 */
export function pickParentOf(p: Pack): string {
  return echParentCommunity(p.name || (p.C.community && p.C.community.name) || "");
}

/**
 * The wizard's home keys → `__homePick`'s newline-joined list.
 *
 * ── GROUPED BY COMMUNITY, NOT RAW PICK ORDER (2026-08-22) ────────────────────────
 * Jack picked ten homes across five communities and the table came back as two
 * sections whose headings did not match their rows — Cove at Southwood homes sitting
 * under "Avalon Woods", Emerald Isles and Silver Falls homes under "Cove at
 * Southwood". The cause is geometry, not naming: row N is pick N, and the section
 * headings are welded to fixed row spans in the block's markup, so a pick order that
 * interleaves communities can never line up with them.
 *
 * So the list is stored GROUPED: communities in the order the requester first touched
 * them, and each community's own picks in the order he chose them. Nothing is dropped
 * and nothing is reordered within a community — the rows simply become contiguous per
 * community, which is what lets a section heading name the rows underneath it.
 */
export function homePickList(packs: Pack[], pickerKeys: string[]): string {
  const order: string[] = [];
  const byParent: Record<string, string[]> = {};
  const seen: Record<string, 1> = {};
  (pickerKeys || []).forEach((k) => {
    const key = String(k || "").trim();
    if (!key || seen[key]) return;
    /* ── A KEY WITHOUT A PACK IS KEPT, NOT DROPPED (2026-08-25) ────────────────────
       A division, via Feedback: "have 6 homes selected for listing table and it only
       added 1 to the table." This used to REQUIRE a pack that contains the home, and
       drop the key otherwise — which is exactly the defect `homePickMerge` was written
       for one path over ("every other community's pick was dropped because the scoped
       wizard's packs could not verify them"), still live on the main Done path. The
       homesites step lists the whole division from `/homesites.json`, so a requester can
       pick homes in communities whose `/sfmc-content` pack was never fetched: five of
       six keys failed verification and vanished silently.
       A key is self-describing — `<code>|h|<addr>|<price>` — so when no pack can vouch
       for it, it is kept and grouped by the code it carries (the same rule
       `homePickMerge` already uses). A malformed key is still refused: that is a bug,
       not inventory. Verification still runs where a pack IS in hand, so a home that has
       genuinely left a loaded feed is still dropped. */
    const pack = packs.find((p) => (p.C.homesites || []).some((h: HomesiteInfo) => homeKey(p.code, h) === key));
    const parts = key.split("|");
    const code = String(parts[0] || "").trim();
    /* `homeKey` always emits FOUR segments (an empty price still leaves the trailing
       pipe), so a 3-part key is malformed and stays refused (audit, 2026-08-25) */
    if (!pack && !(parts.length >= 4 && parts[1] === "h" && code)) return;
    const known = packs.some((p) => String(p.code) === code);
    /* a pack for that community IS loaded and does not have this home → it left the feed */
    if (!pack && known) return;
    seen[key] = 1;
    /* group by the community NAME whenever any pack in hand knows this code — falling
       back to the raw code split one community into two runs and broke the section
       headings the grouping exists for (audit, 2026-08-25) */
    const byCode = packs.find((p) => String(p.code) === code);
    const par = (pack ? pickParentOf(pack) : (byCode ? pickParentOf(byCode) : "")).toLowerCase() || code || "?";
    if (!byParent[par]) { byParent[par] = []; order.push(par); }
    byParent[par].push(key);
  });
  const out: string[] = [];
  order.forEach((par) => byParent[par].forEach((k) => out.push(k)));
  return out.join("\n");
}

/**
 * ADD ONE HOME TO A LIST THAT IS ALREADY STORED. 2026-08-22.
 *
 * "+ Add a home" inside a section must not disturb the homes already in the table —
 * and the first attempt did exactly that. Driven live: adding a Hardwick Farms home
 * left the panel showing ONE section, because the merge round-tripped the stored keys
 * through `homePickList`, which verifies every key against the packs it is handed and
 * DROPS the ones it cannot see. The wizard's packs for a scoped add are that one
 * community's, so every other community's key failed verification and vanished.
 *
 * So the stored lines are trusted as-is — they were verified when they were stored —
 * and only the NEW keys are checked. Grouping still runs, so the added home joins its
 * own community's run; a stored key whose community is not in the packs keeps its
 * original relative position instead of being thrown away.
 */
/**
 * THE ROWS AS THEY STAND → A PICK LIST. 2026-08-22.
 *
 * A listing table normally arrives FED: the rows come from the request's communities
 * and there is no `__homePick` at all. Adding one home to one section then wrote a
 * pick list of exactly that home — and because a pick list is the whole truth, the
 * table collapsed from five sections and ten homes to one section and one home.
 * Driven live twice before this existed.
 *
 * So before an add, the rows already on the block are converted to explicit picks:
 * `__lots` is the fill's own row → real-lot map, which is the only durable link back
 * to the homesite the row came from (the row itself keeps a SHORTENED lot). A row
 * whose lot no longer resolves is skipped rather than guessed at.
 */
export function homePickFromRows(packs: Pack[], data: Record<string, unknown>): string {
  let lots: Record<string, string> = {};
  try { lots = JSON.parse(String(data.__lots || "{}")) as Record<string, string>; } catch { return ""; }
  const out: string[] = [];
  for (let n = 1; n <= 10; n++) {
    const lot = String(lots[String(n)] || "").trim();
    if (!lot) continue;
    /* ── ONLY ROWS THAT ARE STILL ON SCREEN (2026-08-22) ────────────────────────
       `__lots` is written by the fill and NOT rewritten when a row is removed, so
       seeding straight off it resurrected a home Jack had just dropped — measured
       live: dropping 3/Abaco II then adding 11/SIERRA BONUS to the same section
       brought Abaco II back. The row's own Homesite is the current truth. */
    if (!String(data["home" + n + "Homesite"] ?? "").trim()
      && !String(data["home" + n + "Plan"] ?? "").trim()) continue;
    for (const p of packs) {
      const hm = (p.C.homesites || []).find((h) => String((h as { lot?: string }).lot || "") === lot);
      if (!hm) continue;
      const k = homeKey(p.code, hm);
      if (out.indexOf(k) < 0) out.push(k);
      break;
    }
  }
  return out.join("\n");
}

export function homePickMerge(packs: Pack[], storedRaw: unknown, add: string[]): string {
  const stored = String(storedRaw || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const fresh = homePickList(packs, add || []).split("\n").filter(Boolean);
  const all: string[] = [];
  stored.concat(fresh).forEach((k) => { if (all.indexOf(k) < 0) all.push(k); });
  /* group by the community each key names, keeping first-touch order — the same shape
     `homePickList` produces, computed off the KEY's own code so a pack is not needed */
  const order: string[] = [];
  const byCode: Record<string, string[]> = {};
  all.forEach((k) => {
    const code = String(k).split("|")[0] || "?";
    const pack = packs.find((p) => String(p.code) === code);
    const par = (pack ? pickParentOf(pack) : code).toLowerCase();
    if (!byCode[par]) { byCode[par] = []; order.push(par); }
    byCode[par].push(k);
  });
  const out: string[] = [];
  order.forEach((par) => byCode[par].forEach((k) => out.push(k)));
  return out.join("\n");
}

/**
 * THE READ-BACK: a block's STORED pick → the wizard's own key shape. 2026-08-20.
 *
 * WHY IT HAD TO EXIST — a defect found by driving the real path in Chrome after the
 * pane had already been declared working. Step 1 on Market, no community, pick
 * Apalachee Hills → Portland, Done: the canvas correctly read "Portland · 2522 sq ft
 * · 4 beds". Switch to Manual and back to Dynamic and the pane read "Floorplans · 0
 * selected" with no tile, over a canvas still showing Portland. Two renderings of one
 * pick disagreeing — the exact fault this codebase has a standing rule about.
 *
 * The cause: `DynLiveFill` held the picks in COMPONENT state, and its section is not
 * rendered at all in Manual mode, so the component unmounts and the picks die with it.
 * The FEATURE LEVEL had already been moved onto the block (`__featLevel`) for this
 * same reason on 2026-08-20; the picks had not. The block's own `__planPick` /
 * `__homePick` are the durable copy, so the pane seeds from them.
 *
 * HOMES ROUND-TRIP VERBATIM — `homePickList` only joins `homeKey()` strings, which
 * carry no index. A PLAN has to be located again: `__planPick` deliberately stores
 * `num`/`name` rather than a position (see the block comment above), so the index is
 * re-derived here, num first then name, the SAME order `echPlanFor` resolves in. A
 * pick whose pack is not in hand yet returns [] rather than a guessed index — the
 * pane then shows nothing until the pack lands, which is honest, and the CANVAS is
 * unaffected either way because it resolves through `echDynVal`, not through this.
 */
export function pickKeysBack(packs: Pack[], kind: "plans" | "homes", stored: unknown): string[] {
  const raw = String(stored == null ? "" : stored).trim();
  if (!raw) return [];
  if (kind === "homes") return raw.split("\n").map((x) => x.trim()).filter(Boolean);
  /* PLANS ARE A LIST TOO NOW (2026-08-28 — see `planPickList`). A Featured card stores
     one line and reads back one key, exactly as before; the Listing table stores as
     many as the requester picked. A line that cannot be located is dropped rather than
     guessed at, and the rest of the list still comes back — one plan leaving the feed
     must not empty the wizard's selection. */
  const out: string[] = [];
  raw.split("\n").map((x) => x.trim()).filter(Boolean).forEach((line) => {
    const p = line.split("|");
    if (p[1] !== "pn") return;                       /* a legacy `|p|` key, not a pick */
    const pack = packs.find((x) => x.code === String(p[0] || "").trim());
    if (!pack) return;
    const num = String(p[2] || "").trim();
    const name = String(p[3] || "").trim().toLowerCase();
    const plans = pack.C.plans || [];
    let i = num ? plans.findIndex((x) => String(x.num == null ? "" : x.num).trim() === num) : -1;
    if (i < 0 && name) i = plans.findIndex((x) => String(x.name || "").trim().toLowerCase() === name);
    if (i < 0) return;
    const k = pack.code + "|p|" + i;
    if (out.indexOf(k) < 0) out.push(k);
  });
  return out;
}
